/**
 * Capture + Draft module contracts (routes under /api/v1/cd/...).
 * Ported from Pranav's v10.346 Capture tab (thread capture, capture chat, proposal terms, usage import,
 * LinkedIn signals, meeting debrief, business card, event list, lead intake) and Draft tab
 * (generate, deterministic check, hostile judge, revise, purpose brainstorm, harvest facts, save, mark sent).
 *
 * Every AI answer travels in an envelope: `ai: 'ok'` with a result, or `ai: 'unavailable'` with result null.
 * 'unavailable' means Bedrock is not configured OR the model failed validation twice; the UI then shows
 * "AI couldn't help this time — you can do it by hand." Nothing is ever faked.
 */
import { z } from 'zod';
import { Id, IsoDate, VerticalKey } from './common.js';
import { Channel, Direction, Draft } from './activity.js';

export const AiState = z.enum(['ok', 'unavailable']);
export type AiState = z.infer<typeof AiState>;

const Text = (max: number) => z.string().trim().min(1).max(max);
const OptText = (max: number) => z.string().trim().max(max);

export const ChatTurn = z.object({ role: z.enum(['user', 'assistant']), content: Text(8000) }).strict();
export type ChatTurn = z.infer<typeof ChatTurn>;
const History = z.array(ChatTurn).max(30);

/** Draft channels include a phone script (generated only; a script is never saved as a draft). */
export const CdDraftChannel = z.enum(['WhatsApp', 'Email', 'LinkedIn DM', 'Telephone call']);
export type CdDraftChannel = z.infer<typeof CdDraftChannel>;
export const SAVABLE_DRAFT_CHANNELS = ['WhatsApp', 'Email', 'LinkedIn DM'] as const;

// ---------------------------------------------------------------- roster
export const RosterItem = z.object({
  id: Id,
  name: z.string(),
  company: z.string(),
  division: z.string(),
  section: z.string(),
  verticalId: Id,
  verticalKey: VerticalKey,
  touches: z.number().int().nonnegative(),
  ball: z.string(),
  archived: z.boolean(),
});
export type RosterItem = z.infer<typeof RosterItem>;
export const RosterQuery = z.object({ vertical: VerticalKey.optional() });
export const RosterResponse = z.object({ items: z.array(RosterItem), truncated: z.boolean() });
export type RosterResponse = z.infer<typeof RosterResponse>;

// ---------------------------------------------------------------- capture: thread
export const Tone = z.enum(['warm', 'neutral', 'curt', 'frustrated', 'enthusiastic']);
export const CaptureChannel = z.enum(['WhatsApp', 'Email', 'LinkedIn DM', 'Telephone', 'Online demo', 'Personal visit', 'Meeting']);
export const NewPerson = z.object({ name: z.string(), company: z.string(), designation: z.string() });

export const CaptureParseRequest = z.object({
  text: Text(40000),
  relationshipId: Id.optional(),
}).strict();
export const CaptureInteraction = z.object({
  date: IsoDate,
  alreadyRecorded: z.boolean(),
  channel: CaptureChannel,
  dir: Direction,
  summary: z.string(),
  tone: Tone.nullable(),
});
export const CaptureProposal = z.object({
  relationshipId: Id.nullable(),
  newPerson: NewPerson.nullable(),
  matchNote: z.string(),
  interactions: z.array(CaptureInteraction),
  /** 'us' = a reply is owed by us; 'them' = wait. */
  ball: z.enum(['us', 'them']),
  statusHint: z.string(),
  memoryFacts: z.array(z.string()),
  /** Existing remembered lines this thread contradicts (ids resolved by the server). */
  memoryRemovals: z.array(z.object({ id: Id, text: z.string() })),
  watch: z.object({ date: IsoDate, why: z.string() }).nullable(),
  commitments: z.array(z.object({ text: z.string(), by: z.enum(['them', 'us']), dueDate: IsoDate.nullable() })),
});
export type CaptureProposal = z.infer<typeof CaptureProposal>;
export const CaptureParseResponse = z.object({
  ai: AiState,
  result: CaptureProposal.nullable(),
  /** The paste looked like HTML and was stripped to text before reading. */
  strippedHtml: z.boolean(),
  /** The paste was longer than the read limit and was cut. */
  truncated: z.boolean(),
});
export type CaptureParseResponse = z.infer<typeof CaptureParseResponse>;

export const CommitNewPerson = z.object({
  name: Text(200),
  company: OptText(200).default(''),
  designation: OptText(200).optional(),
  phone: OptText(40).optional(),
  email: z.string().trim().email().max(200).optional(),
  /** Kept for old callers; prefer verticalIds. */
  verticalId: Id.optional(),
  /** Every business area this person belongs to. The history is saved on the first one. */
  verticalIds: z.array(Id).min(1).max(20).optional(),
}).strict();
export const CaptureCommitRequest = z.object({
  relationshipId: Id.optional(),
  newPerson: CommitNewPerson.optional(),
  entries: z.array(z.object({ date: IsoDate, dir: Direction, channel: Channel, summary: Text(5000) }).strict()).max(50).default([]),
  facts: z.array(Text(1000)).max(30).default([]),
  retireMemoryIds: z.array(Id).max(30).default([]),
  commitments: z.array(z.object({ text: Text(2000), by: z.enum(['them', 'us']), dueDate: IsoDate.optional() }).strict()).max(20).default([]),
  watch: IsoDate.optional(),
  status: Text(2000).optional(),
  ball: z.enum(['us', 'them']).optional(),
  /** Where this came from, shown on remembered facts ("thread capture", "meeting debrief", ...). */
  source: Text(60),
}).strict().refine((b) => Boolean(b.relationshipId) !== Boolean(b.newPerson), { message: 'Give either relationshipId or newPerson, not both' });
export type CaptureCommitRequest = z.input<typeof CaptureCommitRequest>;
export const CaptureCommitResponse = z.object({
  relationshipId: Id,
  createdPerson: z.boolean(),
  /** Other pipeline entries made for the new person, one per extra business area. */
  otherRelationshipIds: z.array(Id).default([]),
  skippedAreas: z.array(z.object({ verticalId: Id, reason: z.string() })).default([]),
  entries: z.number().int(),
  facts: z.number().int(),
  commitments: z.number().int(),
  retired: z.number().int(),
});
export type CaptureCommitResponse = z.infer<typeof CaptureCommitResponse>;

// ---------------------------------------------------------------- capture chat + keep facts
export const CaptureChatRequest = z.object({ relationshipId: Id.optional(), thread: OptText(40000).default(''), history: History.min(1) }).strict();
export const ReplyResponse = z.object({ ai: AiState, reply: z.string().nullable() });
export type ReplyResponse = z.infer<typeof ReplyResponse>;
export const KeepFactsRequest = z.object({ kind: z.enum(['capture', 'draft']), relationshipId: Id, history: History.min(1) }).strict();
export const FactsResponse = z.object({ ai: AiState, facts: z.array(z.string()).nullable() });
export type FactsResponse = z.infer<typeof FactsResponse>;
export const SaveFactsRequest = z.object({ relationshipId: Id, facts: z.array(Text(1000)).min(1).max(30), source: Text(60) }).strict();
export const SaveFactsResponse = z.object({ saved: z.number().int() });

// ---------------------------------------------------------------- debrief
export const TextRequest = z.object({ text: Text(40000), relationshipId: Id.optional() }).strict();
export const DebriefEntry = z.object({
  relationshipId: Id.nullable(),
  newPerson: NewPerson.nullable(),
  channel: z.enum(['Meeting', 'Telephone']),
  summary: z.string(),
  tone: Tone.nullable(),
  facts: z.array(z.string()),
  concernsHeard: z.string(),
  resonated: z.string(),
  commitment: z.string(),
  theirWords: z.string(),
  watch: IsoDate.nullable(),
});
export const DebriefResponse = z.object({ ai: AiState, result: z.object({ entries: z.array(DebriefEntry) }).nullable() });
export type DebriefResponse = z.infer<typeof DebriefResponse>;

// ---------------------------------------------------------------- usage import
export const UsageResponse = z.object({
  ai: AiState,
  result: z.object({
    matched: z.array(z.object({ relationshipId: Id, usageLine: z.string() })),
    unmatched: z.array(z.object({ name: z.string(), email: z.string(), oneLine: z.string() })),
  }).nullable(),
});
export type UsageResponse = z.infer<typeof UsageResponse>;

// ---------------------------------------------------------------- LinkedIn signals
export const Warmth = z.enum(['watching', 'warming', 'engaged', 'hot']);
export const SignalKind = z.enum(['like', 'comment', 'connection_accept', 'profile_view', 'dm', 'job_change', 'other']);
export const LinkedinResponse = z.object({
  ai: AiState,
  /** The paste was a direct conversation, which belongs in thread capture instead. */
  result: z.object({
    signals: z.array(z.object({ relationshipId: Id, kind: SignalKind, detail: z.string(), newWarmth: Warmth, why: z.string() })),
    unmatched: z.array(z.object({ name: z.string(), detail: z.string(), icpFit: z.boolean() })),
    commentQueue: z.array(z.object({ name: z.string(), relationshipId: Id.nullable(), postTopic: z.string(), why: z.string(), commentDraft: z.string() })),
    overflowNote: z.string(),
  }).nullable(),
});
export type LinkedinResponse = z.infer<typeof LinkedinResponse>;
export const LinkedinCommitRequest = z.object({
  signals: z.array(z.object({ relationshipId: Id, kind: SignalKind, detail: Text(500), newWarmth: Warmth }).strict()).max(40).default([]),
  comments: z.array(z.object({ name: Text(200), relationshipId: Id.nullable(), postTopic: OptText(500), why: OptText(500), commentDraft: Text(2000), verticalId: Id }).strict()).max(5).default([]),
}).strict();
export const LinkedinCommitResponse = z.object({ signals: z.number().int(), warmthRaised: z.number().int(), comments: z.number().int() });

// ---------------------------------------------------------------- business card
export const CardResponse = z.object({
  ai: AiState,
  result: z.object({
    name: z.string(), company: z.string(), designation: z.string(), phone: z.string(), email: z.string(), city: z.string(),
    existingId: Id.nullable(),
  }).nullable(),
});
export type CardResponse = z.infer<typeof CardResponse>;

// ---------------------------------------------------------------- event list
export const EventResponse = z.object({
  ai: AiState,
  result: z.object({
    known: z.array(z.object({ relationshipId: Id, note: z.string() })),
    icpNew: z.array(z.object({ name: z.string(), company: z.string(), designation: z.string(), why: z.string() })),
  }).nullable(),
});
export type EventResponse = z.infer<typeof EventResponse>;

// ---------------------------------------------------------------- proposal terms
export const TermsResponse = z.object({ ai: AiState, result: z.object({ terms: z.array(z.object({ label: z.string(), fact: z.string() })) }).nullable() });
export type TermsResponse = z.infer<typeof TermsResponse>;

// ---------------------------------------------------------------- people
export const CdAddPersonRequest = CommitNewPerson.extend({
  /** O = new, not contacted yet; E = recently contacted. */
  section: z.enum(['O', 'E']).default('O'),
  note: OptText(1000).optional(),
  origin: OptText(200).optional(),
}).strict();
export type CdAddPersonRequest = z.input<typeof CdAddPersonRequest>;
export const CdAddPersonResponse = z.object({
  relationshipId: Id, contactId: Id, reusedContact: z.boolean(),
  otherRelationshipIds: z.array(Id).default([]),
  skippedAreas: z.array(z.object({ verticalId: Id, reason: z.string() })).default([]),
});
export const AttachDetailsRequest = z.object({
  relationshipId: Id,
  phone: OptText(40).optional(),
  email: z.string().trim().email().max(200).optional(),
  designation: OptText(200).optional(),
}).strict();

// ---------------------------------------------------------------- queue (lead intake + comment-back)
export const QueueKind = z.enum(['lead', 'comment']);
export const QueueItem = z.object({
  id: Id,
  kind: QueueKind,
  verticalId: Id,
  status: z.enum(['open', 'added', 'dismissed', 'done']),
  name: z.string(),
  company: z.string(),
  score: z.enum(['hot', 'warm', 'cold']).nullable(),
  source: z.string(),
  why: z.string(),
  action: z.string(),
  relationshipId: Id.nullable(),
  createdAt: z.string(),
});
export type QueueItem = z.infer<typeof QueueItem>;
export const QueueQuery = z.object({ kind: QueueKind, status: z.enum(['open', 'added', 'dismissed', 'done']).default('open') });
export const QueueResponse = z.object({ items: z.array(QueueItem) });
export const LeadScoreRequest = z.object({ text: Text(40000), verticalId: Id }).strict();
export const LeadScoreResponse = z.object({
  ai: AiState,
  queued: z.array(QueueItem),
  known: z.array(z.object({ name: z.string(), relationshipId: Id })),
  skippedDuplicates: z.number().int(),
});
export type LeadScoreResponse = z.infer<typeof LeadScoreResponse>;
/** Accepting a lead may add it to several business areas; the lead's own area is always one of them. */
export const QueueAcceptRequest = z.object({ verticalIds: z.array(Id).max(20).optional() }).strict();
export type QueueAcceptRequest = z.infer<typeof QueueAcceptRequest>;
export const QueueAcceptResponse = z.object({
  item: QueueItem, relationshipId: Id.nullable(),
  otherRelationshipIds: z.array(Id).default([]),
  skippedAreas: z.array(z.object({ verticalId: Id, reason: z.string() })).default([]),
});

// ---------------------------------------------------------------- draft
export const DraftWarning = z.object({ text: z.string(), law: z.string().nullable(), tone: z.enum(['info', 'warning', 'danger']) });
export const DraftContextResponse = z.object({
  relationshipId: Id,
  name: z.string(),
  company: z.string(),
  section: z.string(),
  type: z.string().nullable(),
  warnings: z.array(DraftWarning),
  purposes: z.array(z.string()),
  lastOutbound: z.object({ date: IsoDate, channel: z.string(), summary: z.string() }).nullable(),
});
export type DraftContextResponse = z.infer<typeof DraftContextResponse>;
export const DraftContextQuery = z.object({ purpose: OptText(300).optional(), channel: CdDraftChannel.optional() });

export const Judge = z.object({ verdict: z.enum(['pass', 'fail']), issues: z.array(z.object({ issue: z.string(), fix: z.string() })) });
export type Judge = z.infer<typeof Judge>;

export const GenerateDraftRequest = z.object({
  relationshipId: Id,
  channel: CdDraftChannel,
  purpose: Text(300),
  context: OptText(3000).optional(),
  recipient: OptText(200).optional(),
  /** internal = a note to a colleague about this person; never a client message. */
  mode: z.enum(['outward', 'internal']).default('outward'),
  internalTo: OptText(100).optional(),
  previousDraft: OptText(10000).optional(),
}).strict();
export type GenerateDraftRequest = z.input<typeof GenerateDraftRequest>;
export const GenerateDraftResponse = z.object({
  ai: AiState,
  /** The engine refused: the last message already asked the same thing. Shown as a verdict, not a message. */
  noDraft: z.string().nullable(),
  body: z.string().nullable(),
  /** Deterministic rule check (packages/core checkDraft), run on the server. */
  hits: z.array(z.string()),
  judge: Judge.nullable(),
  /** false when the judge could not run: read the draft with double care. */
  judgeRan: z.boolean(),
});
export type GenerateDraftResponse = z.infer<typeof GenerateDraftResponse>;

export const CheckDraftRequest = z.object({ relationshipId: Id, channel: CdDraftChannel, body: Text(10000) }).strict();
export const CheckDraftResponse = z.object({ hits: z.array(z.string()) });

export const JudgeDraftRequest = z.object({
  relationshipId: Id, channel: CdDraftChannel, body: Text(10000), recipient: OptText(200).optional(),
  applied: z.array(Text(1000)).max(30).default([]), skipped: z.array(Text(1000)).max(30).default([]), round: z.number().int().min(1).max(20).default(1),
}).strict();
export const JudgeDraftResponse = z.object({ ai: AiState, judge: Judge.nullable() });

export const RevisePreset = z.enum(['simple', 'human', 'warmer', 'formal']);
export const ReviseDraftRequest = z.object({
  relationshipId: Id, channel: CdDraftChannel, draft: Text(10000), history: History.default([]),
  message: OptText(4000).optional(), preset: RevisePreset.optional(), recipient: OptText(200).optional(),
}).strict().refine((b) => Boolean(b.message) || Boolean(b.preset), { message: 'Give a message or a preset' });
export type ReviseDraftRequest = z.input<typeof ReviseDraftRequest>;
export const ReviseDraftResponse = z.object({ ai: AiState, reply: z.string().nullable(), draft: z.string().nullable(), hits: z.array(z.string()) });
export type ReviseDraftResponse = z.infer<typeof ReviseDraftResponse>;

export const PurposeRequest = z.object({ relationshipId: Id, history: History.min(1), recipient: OptText(200).optional() }).strict();
export const PurposeResponse = z.object({ ai: AiState, reply: z.string().nullable(), purposeReady: z.string().nullable() });
export type PurposeResponse = z.infer<typeof PurposeResponse>;

export const SaveDraftRequest = z.object({
  relationshipId: Id,
  channel: z.enum(SAVABLE_DRAFT_CHANNELS),
  purpose: Text(200),
  body: Text(10000),
}).strict();
export const SaveDraftResponse = z.object({ draft: Draft, hits: z.array(z.string()) });
export type SaveDraftResponse = z.infer<typeof SaveDraftResponse>;

export const SaveContextRequest = z.object({ relationshipId: Id, text: Text(3000) }).strict();

/** Route table for this module (paths are under /api/v1). */
export const cdRoutes = {
  cdRoster: { method: 'GET', path: '/cd/roster' },
  cdCaptureParse: { method: 'POST', path: '/cd/capture/parse' },
  cdCaptureCommit: { method: 'POST', path: '/cd/capture/commit' },
  cdCaptureChat: { method: 'POST', path: '/cd/capture/chat' },
  cdKeepFacts: { method: 'POST', path: '/cd/facts/extract' },
  cdSaveFacts: { method: 'POST', path: '/cd/facts/save' },
  cdDebrief: { method: 'POST', path: '/cd/capture/debrief' },
  cdUsage: { method: 'POST', path: '/cd/capture/usage' },
  cdLinkedin: { method: 'POST', path: '/cd/capture/linkedin' },
  cdLinkedinCommit: { method: 'POST', path: '/cd/capture/linkedin/commit' },
  cdCard: { method: 'POST', path: '/cd/capture/card' },
  cdEvent: { method: 'POST', path: '/cd/capture/event' },
  cdTerms: { method: 'POST', path: '/cd/capture/terms' },
  cdAddPerson: { method: 'POST', path: '/cd/people' },
  cdAttachDetails: { method: 'POST', path: '/cd/people/attach' },
  cdLeadScore: { method: 'POST', path: '/cd/leads/score' },
  cdQueue: { method: 'GET', path: '/cd/queue' },
  cdQueueAccept: { method: 'POST', path: '/cd/queue/:id/accept' },
  cdQueueDismiss: { method: 'POST', path: '/cd/queue/:id/dismiss' },
  cdDraftContext: { method: 'GET', path: '/cd/draft/context/:id' },
  cdDraftGenerate: { method: 'POST', path: '/cd/draft/generate' },
  cdDraftCheck: { method: 'POST', path: '/cd/draft/check' },
  cdDraftJudge: { method: 'POST', path: '/cd/draft/judge' },
  cdDraftRevise: { method: 'POST', path: '/cd/draft/revise' },
  cdDraftPurpose: { method: 'POST', path: '/cd/draft/purpose' },
  cdDraftSave: { method: 'POST', path: '/cd/draft/save' },
  cdSaveContext: { method: 'POST', path: '/cd/draft/context-note' },
} as const;
